/**
 * ADD TO CART: ONE COPY, SHARED BY THE PORTRAIT PAGE AND THE HOODIE PAGE.
 *
 * ── WHY IT LEFT sections/ts-product.liquid ──────────────────────────────────────────────────────
 *
 * That section is 2,845 lines and almost all of it is wall art: to-scale room renders, frame
 * corners, printed-name positioning, border-artwork probing. A hoodie needs none of it, so merch
 * sells from its own section — but THIS part is genuinely shared, and it carries fixes that were
 * expensive to learn and are invisible if you only read the happy path:
 *
 *   · the payload is `new FormData(form)` UNMODIFIED, because this form signals "do not submit this
 *     field" by setting `disabled`, and a hand-built object would start submitting fields the
 *     customer turned off;
 *   · there is NO blind re-submit on an ambiguous failure, because the add may already have landed;
 *   · the photo is shrunk before the payload is built, so FormData holds the smaller blob;
 *   · the drawer opens at submit rather than at response, because the upload is seconds long and
 *     the click otherwise appears to do nothing.
 *
 * Two copies would mean the next fix gets made once. Hence one file, called by both.
 *
 * ── THE SEAMS, EACH NAMED RATHER THAN ASSUMED ──────────────────────────────────────────────────
 *
 *   form           the <form> to submit. Required.
 *   title          the product title, for the optimistic cart row.
 *   identity       { el, mint } on a page that carries a committed print-file URL, or NULL.
 *   previewEl      optional hidden input holding the mockup that was on screen.
 *   stampPreview   optional; fills that input. Wall art only — it reads the mockup manifest.
 *   onCartChanged  optional; the page's own recompute after the cart changes.
 *
 * Everything else is looked up by id, and both pages use the same ids for the same things.
 *
 * ── WHY `identity` MAY BE NULL, AND WHAT THAT COSTS ────────────────────────────────────────────
 *
 * Recovery from an ambiguous failure uses the per-submit `_PrintFile` uuid as an idempotency key:
 * fetch /cart.js, look for it, and offer a retry only once that has PROVEN nothing was added.
 *
 * A hoodie has no such uuid ON PURPOSE. Merch sends no `_PrintFile` — that is exactly what lets
 * Shopify merge two adds of the same hoodie into one line at quantity 2, which is what makes "same
 * pet, one job" true. So a hoodie cannot self-reconcile, and must NOT be offered a retry: re-posting
 * would raise the quantity and make a second garment nobody bought.
 *
 * With no identity, `reconcile` answers "unknown" and the customer is asked to check their cart —
 * the same conservative branch this file already takes when reconciliation itself fails. A worse
 * message in a rare failure is the right trade against a duplicate parcel.
 */
/* ── THE WIZARD'S OWN EVENTS (2026-09-05) ──────────────────────────────────────────────────────
 *
 * One GA4 event per completed step, so the studio can see where a customer stops INSIDE the product
 * page (Marketing › Website, "Inside the product page"). They ride the Google channel's own tag:
 * that pixel runs in the OPEN context and loads gtag.js into this window (measured 2026-09-05:
 * window.gtag, dataLayer, the _ga cookies), so a push here lands in the same client and session —
 * no second tag, no double counting.
 *
 * Two rules of gtag.js decide the shape: only a REAL Arguments object in the layer is a command
 * (an array is ignored), and an event that arrives before its target's `config` is DROPPED, not
 * queued — the pixel can boot late, or wait for consent. So events queue until `config G-…` sits
 * in the layer, and every event names its target so the Ads tag never sees it. Once per page load
 * per name; never throws. studio/scripts/test-wizard-events.ts pins the names and this behaviour.
 */
/* TS_TRACKER_START */
(function(){
  var TS_GA4 = 'G-Y1Q8PXMWL4';
  var fired = {}, queue = [], tries = 0;
  function push(){ (window.dataLayer = window.dataLayer || []).push(arguments); }
  function configured(){
    var dl = window.dataLayer || [];
    for (var i = 0; i < dl.length; i++) { var a = dl[i]; if (a && a[0] === 'config' && a[1] === TS_GA4) return true; }
    return false;
  }
  function flush(){
    try {
      if (!configured()) { if (tries < 40) { tries++; setTimeout(flush, 500); } return; }
      while (queue.length) { var q = queue.shift(); push('event', q[0], q[1]); }
    } catch (_) {}
  }
  window.tsTrack = function (name, params) {
    try {
      if (!name || fired[name]) return;
      fired[name] = true;
      var p = { send_to: TS_GA4 }, d = window.tsTrack.defaults || {}, k;
      for (k in d) p[k] = d[k];
      for (k in (params || {})) p[k] = params[k];
      queue.push([name, p]);
      flush();
    } catch (_) {}
  };
})();
/* TS_TRACKER_END */

/* ── THE PET'S NAME, NORMALISED — ONE IMPLEMENTATION FOR EVERY PAGE THAT SHOWS ONE ─────────────
 *
 * São Torpes has ’ “ ” but NOT ' " , and a missing glyph is not a blank: opentype.js draws .notdef,
 * a TOFU BOX, onto the paper. The storefront used to hide that completely — its stack is
 * `'Sao Torpes',sans-serif` and browsers fall back PER CHARACTER, so an apostrophe was quietly drawn
 * in a system font and looked perfectly fine. "Lily's" previewed correctly and printed "LILY⊠S".
 *
 * ⚠ IT LIVES HERE BECAUSE A SECOND PAGE NOW SHOWS A NAME. The wall-art page had this inline, which
 * was one copy against the server's `normalisePetName` in studio/lib/name-text.ts and was held to it
 * by scripts/test-theme-contract.ts. The mug's preview needs the same answer, and a copy pasted into
 * a second section would be a third thing to keep in step with nothing holding it there — every page
 * that loads this file now calls the same function, and the contract test runs THIS one against the
 * server's.
 *
 * Both sections load this script immediately before their own, and classic scripts run in order, so
 * it is defined by the time either of them parses.
 */
window.tsPrintableName = function printableName(raw){
    if(!raw) return '';
    var out = raw.replace(/['`´‘]/g, '’');
    out = out.replace(/"/g, function(m, i){
      return (i === 0 || /[\s([{]/.test(out[i-1])) ? '“' : '”';
    });
    out = out.replace(/–/g, '-');            // en dash -> hyphen; Sao Torpes has - and — but not –
    return out.replace(/\s+/g, ' ').trim();
  };

/* ── THE POSSESSIVE, FOR THE PET PARENT MUGS ───────────────────────────────────────────────────
 *
 * "Winston" -> "Winston’s". The customer types their pet's name and the mug says whose mug it is,
 * so the apostrophe is added rather than typed.
 *
 * ⚠ THIS IS THE THEME'S HALF OF studio/lib/name-text.ts `possessive()`, and the two are asserted
 * equal by studio/scripts/test-theme-contract.ts — the same arrangement `printableName` above has,
 * and for the same reason: two implementations of one rule is the shape that drifts, and drift here
 * means the preview and the printed mug disagree about the customer's own pet's name.
 *
 * ⚠ ALWAYS ’S, INCLUDING AFTER A FINAL S. Chicago writes "Gus’s" and AP writes "Gus’", and neither
 * is wrong — ’S wins because the mug is read aloud and because one rule cannot pick the wrong one
 * of two acceptable forms by mis-detecting a final S. A name that already ends in ’s is left alone.
 */
window.tsPossessive = function possessive(raw){
    var n = window.tsPrintableName(raw);
    if(!n) return '';
    return /’s$/i.test(n) ? n : n + '’s';
  };

/* ── HOW BIG THE NAME IS SET, SHARED BY EVERY PAGE THAT SHOWS ONE ──────────────────────────────
 *
 * The band gives a name its natural size; a name too long for the band is set smaller so it fits
 * (João, 2026-09-10). ⚠ THE SHRINK ITSELF IS NOT DECIDED HERE — `nameLayout()` in
 * studio/lib/name-layout.ts owns it, and its rule is one division: `min(natural, bandWidth / em)`,
 * where `em` is the name's width at font-size 1. What this function does is MEASURE that width the
 * way the browser will actually paint it, so the two surfaces can agree without either of them
 * measuring for the other.
 *
 * ⚠ IT MUST MEASURE THE SAME QUANTITY THE SERVER SUMS, or the customer approves one size and
 * receives another — the same class of failure the name normalising above exists for. Three things
 * make them the same number:
 *
 *   · `fontKerning = 'none'`, because the server sums raw advance widths and never kerns.
 *   · tracking added after EVERY character INCLUDING the last, which is what CSS lays out and what
 *     `drawName` sums; the centring on both sides then divides that trailing space.
 *   · the face's own casing applied first, because an uppercasing face measures its capitals.
 *
 * ⚠ AND THE FACE HAS TO BE LOADED. `measureText` against a font still in flight silently measures
 * the fallback, which on a long name is a wrong shrink baked into what the customer sees. Callers
 * re-run themselves on `document.fonts.ready`; this returns the natural size rather than guessing
 * when it can tell the face is not there yet.
 */
var _tsNameCtx = null;
window.tsNameFontSize = function (text, o) {
  var natural = o && o.natural;
  if (!text || !natural || !(o.bandW > 0)) return natural;
  try {
    if (!_tsNameCtx) _tsNameCtx = document.createElement('canvas').getContext('2d');
    if (!_tsNameCtx) return natural;
    var label = o.upper ? String(text).toUpperCase() : String(text);
    var font = (o.weight || '400') + ' 100px ' + o.family;
    if (document.fonts && document.fonts.check && !document.fonts.check(font)) return natural;
    _tsNameCtx.fontKerning = 'none';
    _tsNameCtx.font = font;
    /* Code points, not UTF-16 units: the server iterates the string with a spread, so an emoji or an
       accented pair must count once on both sides or the tracking totals differ. */
    var chars = Array.from(label).length;
    var em = _tsNameCtx.measureText(label).width / 100 + (o.tracking || 0) * chars;
    if (!(em > 0)) return natural;
    return Math.min(natural, o.bandW / em);
  } catch (e) { return natural; }
};

window.tsAddToCart = function (opts) {
  var form = opts.form;
  if (!form) return;
  /* German, and any later language, arrives as window.TS_I18N (snippets/ts-i18n.liquid prints it on
     every page that is NOT in the store's first language). English keeps the sentences written below,
     so an English visitor runs exactly the strings this file always ran. */
  var I18N = window.TS_I18N || {};
  function tr(key, en, vars) {
    var s = I18N[key] || en;
    for (var k in vars || {}) s = s.split('{' + k + '}').join(vars[k]);
    return s;
  }
  /* The wizard's own events: `wizard_<product>_<step>` (the tracker above). A page that passes no
     `analytics` sends nothing. */
  var analytics = opts.analytics || {};
  if (window.tsTrack) window.tsTrack.defaults = { style: analytics.style || '' };
  function track(step){ try { if (analytics.product && window.tsTrack) window.tsTrack('wizard_' + analytics.product + '_' + step); } catch (_) {} }
  var identity = opts.identity || null;
  var previewEl = opts.previewEl || null;
  var printFileEl = identity ? identity.el : null;
  var mint = function () { if (identity) identity.mint(); };

  /* Looked up here rather than passed in: both pages use these ids for the same things, and a page
     that renamed one would break its own upload long before it reached the cart. The browser is the
     only thing that can catch a missing one — this file has no compiler over it — which is exactly
     how `previewEl`, `file`, `upErr`, `upWrap`, `addBtn` and the pending-row spec all slipped
     through the extraction and only surfaced on a real add-to-cart. */
  var file   = document.getElementById('ts-file');
  var upWrap = document.getElementById('ts-upload-wrap');
  var upErr  = document.getElementById('ts-upload-error');
  var addBtn = document.getElementById('wizAdd');

  /* ── THE PHOTO FIELD: thumbnail, drag & drop, Replace/Remove, the label⇄filled swap ──────────
     Moved here from sections/ts-product.liquid on 2026-09-01, because the merch page had grown its
     own shorter copy and the two had drifted apart: the hoodie pages shipped with NO drop handlers
     (an element whose dragover never calls preventDefault is not a drop target, so dragging a photo
     onto them did nothing), never hid #ts-upload-label (so the "Photo added" panel stacked below a
     still-visible dropzone), never revoked an object URL, and showed HEIC uploads as a broken image.
     The dropzone MARKUP was byte-identical in both sections the whole time — one implementation was
     always the right number. Both pages call tsAddToCart() exactly once, which is what runs this;
     a page without the upload markup no-ops on the guards below. */
  var upLabel  = document.getElementById('ts-upload-label');
  var upFilled = document.getElementById('ts-upload-filled');
  var upThumb  = document.getElementById('ts-upload-thumb');
  var upFname  = document.getElementById('ts-upload-fname');
  var thumbUrl = null;
  function clearThumbUrl(){ if(thumbUrl){ URL.revokeObjectURL(thumbUrl); thumbUrl=null; } }
  function showEmpty(){
    if(upFilled) upFilled.hidden=true;
    if(upLabel) upLabel.hidden=false;
    clearThumbUrl();
    if(upThumb){ upThumb.removeAttribute('src'); upThumb.hidden=true; }
  }
  function showFilled(f){
    track('photo');
    if(upErr) upErr.hidden=true;
    if(upWrap) upWrap.classList.remove('is-invalid');
    if(upFname) upFname.textContent=f.name;
    if(upLabel) upLabel.hidden=true;
    if(upFilled) upFilled.hidden=false;
    // Try a thumbnail; HEIC (iPhone) and a few formats won't decode in-browser -> keep the icon-only success state.
    clearThumbUrl();
    if(upThumb){
      try{
        thumbUrl=URL.createObjectURL(f);
        upThumb.onload=function(){ upThumb.hidden=false; };
        upThumb.onerror=function(){ upThumb.hidden=true; clearThumbUrl(); };
        upThumb.src=thumbUrl;
      }catch(e){ upThumb.hidden=true; }
    }
  }
  function applyFile(){
    if(file && file.files && file.files[0]) showFilled(file.files[0]);
    else showEmpty();
  }
  if(file) file.addEventListener('change', applyFile);
  var upReplace=document.getElementById('ts-upload-replace'), upRemove=document.getElementById('ts-upload-remove');
  if(upReplace) upReplace.addEventListener('click', function(){ if(file) file.click(); });   // re-open picker
  if(upRemove) upRemove.addEventListener('click', function(){ if(file){ file.value=''; } showEmpty(); });
  // Drag & drop over the whole upload area. Assign to the input's .files via DataTransfer so the
  // native required/submit still see the file, then run the same applyFile path.
  if(upWrap && file && ('DataTransfer' in window)){
    ['dragenter','dragover'].forEach(function(ev){ upWrap.addEventListener(ev, function(e){ e.preventDefault(); e.stopPropagation(); upWrap.classList.add('dragover'); }); });
    ['dragleave','dragend'].forEach(function(ev){ upWrap.addEventListener(ev, function(e){ e.preventDefault(); e.stopPropagation(); if(ev==='dragleave' && e.relatedTarget && upWrap.contains(e.relatedTarget)) return; upWrap.classList.remove('dragover'); }); });
    upWrap.addEventListener('drop', function(e){
      e.preventDefault(); e.stopPropagation(); upWrap.classList.remove('dragover');
      var dtf=e.dataTransfer && e.dataTransfer.files;
      if(dtf && dtf[0]){
        try{ var dt=new DataTransfer(); dt.items.add(dtf[0]); file.files=dt.files; }catch(err){}
        applyFile();
      }
    });
  }

  /* The free-shipping bar's running total belongs to the PAGE — it is rendered from `cart.total_price`
     in Liquid and read by the page's own progress bar — but the cart is what changes it. One seam
     with a getter and a setter rather than two callbacks, so both the absolute and the delta case
     go through the same door. */
  var cartTotal = opts.cartTotal || { get: function(){ return 0; }, set: function(){} };
  /* The free-shipping bar counts THE WHOLE CART, non-shippable lines included, because that is what
     Shopify's rate condition counts. The condition is TOTAL_PRICE >= $100 and it was measured at a
     real checkout on 2026-08-29: $89 portrait + $25 gift card = $114 gets FREE shipping, and $75
     print + $29 Digital File = $104 does too.

     This function used to sum only lines where requires_shipping, on the belief that checkout would
     withhold free shipping from those dollars. It does not, and the bar consequently told customers
     who already qualified that they were short. Long note in snippets/ts-free-shipping.liquid; all
     four surfaces have to count the same dollars or they contradict each other. */
  function cartCountingTotal(cart){
    return (cart && cart.total_price) || 0;
  }

  /* properties[_Preview] — the mockup URL for the configuration being added, so the cart can tell
     two portraits apart. Display only; see the hidden input above.

     Absolutised deliberately. PHOTO_MANIFEST values come from asset_url, which emits a
     PROTOCOL-RELATIVE "//shop.myshopify.com/cdn/..." — fine in a browser, broken the moment it is
     stored and read anywhere that has no page protocol to inherit. */

  /* ═══ ADD TO CART ═════════════════════════════════════════════════════════════════════════════

     Uploads the photo over XHR and opens the cart drawer, falling back to the original native
     multipart POST when the drawer is not available.

     WHY XHR AND NOT fetch. fetch cannot report upload progress in any shipping browser — the only
     mechanism is a streamed request body, which is Chromium-only and would mean hand-serialising
     the multipart envelope. xhr.upload.onprogress is universal and needs no change to how the
     payload is built. The defect being fixed is a 5-15s upload on mobile data with nothing on
     screen, so progress IS the feature; using fetch would mean shipping the bug with extra steps.

     THE PAYLOAD IS `new FormData(form)`, UNMODIFIED. That matters more than it looks: this form
     signals "do not submit this field" by setting `disabled` (the implied frame colour, the pet
     name, the name font and ink, in four places). FormData honours disabled exactly as a native
     POST does. A hand-built object would silently start submitting fields the customer turned off.
     It would also skip the portrait page's `formdata` listener, which writes Name option and drops
     the name fields when the box is empty (ts-product.liquid): that event fires only while the
     browser itself builds the entry list.

     THERE IS NO NATIVE RE-SUBMIT FALLBACK, DELIBERATELY. On an ambiguous failure the add may
     already have landed. Re-posting the same _PrintFile would make Shopify MERGE the two adds into
     one line at quantity 2 with a single print file — the exact collapse the comment above
     mintPrintFile exists to prevent, arriving through a new door. Minting a second uuid instead
     produces two paid lines nobody asked for. So the recovery is reconciliation, not retry:
     /cart.js is fetched and the uuid used as an idempotency key, and a retry is only offered once
     that has PROVEN nothing was added. */

  var submitting=false;
  var pendingPrintFile=null;   // uuid of an attempt whose outcome is not yet known
  var addLabel=document.getElementById('wizAddLabel');
  var addFill=document.getElementById('wizAddFill');
  var addErrEl=document.getElementById('wizAddError');
  var addStatus=document.getElementById('wizAddStatus');
  /* No page promises "up to 20MB" next to the file input any more: the portrait page dropped that line on
     2026-09-15 and the merch pages on 2026-09-16 (Just Lyne's upload words), so this check is the only
     place the limit shows. Checking here turns a 15-second upload that ends in a server rejection into
     an instant, specific message. */
  var MAX_UPLOAD_BYTES=20*1024*1024;

  /* #ts-file is `required` and is a rendered 1x1 element, so the browser blocks submission with its
     own bubble and the submit event never fires — which means the styled guard below has been
     unreachable. novalidate is set from JS only, so with JS dead `required` still backstops. */
  try{ form.setAttribute('novalidate','novalidate'); }catch(_){}

  function setLabel(t){ if(addLabel) addLabel.textContent=t; }
  function announce(t){ if(addStatus) addStatus.textContent=t; }
  function setFill(pct){ if(addFill) addFill.style.width=(pct==null?0:pct)+'%'; }
  function showAddError(msg){
    if(!addErrEl) return;
    addErrEl.textContent=msg; addErrEl.hidden=false;
    try{ addErrEl.scrollIntoView({behavior:'smooth',block:'center'}); }catch(_){}
  }
  function clearAddError(){ if(addErrEl){ addErrEl.hidden=true; addErrEl.textContent=''; } }
  function busy(on){
    if(!addBtn) return;
    addBtn.classList.toggle('is-busy', !!on);
    if(on){ addBtn.setAttribute('aria-busy','true'); addBtn.setAttribute('aria-disabled','true'); }
    else { addBtn.removeAttribute('aria-busy'); addBtn.removeAttribute('aria-disabled'); }
  }
  /* aria-disabled, never `disabled`. The original reason for avoiding it (disabling a submit button
     inside its own handler cancels the native post) no longer applies once preventDefault is
     unconditional — but `disabled` drops the button from the tab order, so a keyboard or screen
     reader user is dumped onto <body> mid-upload. The `submitting` flag is what actually blocks
     re-entry, and it also covers the Enter key. */
  function resetAddBtn(){
    busy(false); if(addBtn) addBtn.classList.remove('is-waiting');
    setFill(0); setLabel(tr('atc_add', 'Add to cart'));
  }

  /* ── SHRINK THE PHOTO BEFORE UPLOADING IT ─────────────────────────────────────────────────────

     The photo is a field of the same POST that returns the drawer, so the drawer cannot appear
     before the last byte of it has gone. A modern phone photo is 3-12MB and was being sent byte
     for byte, which is where "add to cart takes ages" came from.

     Safe to resize because the customer's photo is never what gets printed. It is an INPUT to
     background removal (remove.bg, studio/lib/intake.ts) and then to image generation, which reads
     it as base64 at modest resolution; what prints is the generated artwork at _PrintFile. 2400px
     on the long edge is comfortably above what either consumer uses, and turns a 10MB upload into
     roughly 1MB.

     ⚠ EVERY FAILURE PATH RETURNS THE ORIGINAL FILE. This runs on the one irreplaceable thing the
     customer gives us, so it is written to be incapable of losing it: an unsupported format, a
     decode failure, a null blob, or a result that is somehow larger all fall through untouched.
     HEIC is the expected case, not an edge case — it is the iPhone default and no browser decodes
     it, so createImageBitmap simply rejects and the original goes up as before. */
  var MAX_UPLOAD_EDGE=2400;
  function shrinkPhoto(file){
    return new Promise(function(resolve){
      if(!file || !window.createImageBitmap || !window.HTMLCanvasElement){ resolve(file); return; }
      if(!/^image\//.test(file.type||'')){ resolve(file); return; }
      // imageOrientation:'from-image' applies the EXIF rotation. Without it a portrait phone photo
      // decodes sideways and we would bake that rotation into the file we send.
      createImageBitmap(file, {imageOrientation:'from-image'}).then(function(bmp){
        var long=Math.max(bmp.width, bmp.height);
        if(long<=MAX_UPLOAD_EDGE){ bmp.close && bmp.close(); resolve(file); return; }
        var s=MAX_UPLOAD_EDGE/long;
        var c=document.createElement('canvas');
        c.width=Math.round(bmp.width*s); c.height=Math.round(bmp.height*s);
        var ctx=c.getContext('2d');
        if(!ctx){ bmp.close && bmp.close(); resolve(file); return; }
        ctx.drawImage(bmp,0,0,c.width,c.height);
        bmp.close && bmp.close();
        c.toBlob(function(blob){
          // Only take the resized version if it is genuinely smaller. An already-optimised JPEG can
          // come back larger from a re-encode, and sending that would be worse than doing nothing.
          if(!blob || blob.size>=file.size){ resolve(file); return; }
          try{
            resolve(new File([blob], String(file.name||'photo.jpg').replace(/\.[^.]+$/,'')+'.jpg', {type:'image/jpeg'}));
          }catch(_){ resolve(blob); }   // File constructor is unavailable on some older browsers
        }, 'image/jpeg', 0.92);
      }).catch(function(){ resolve(file); });   // HEIC and anything else undecodable
    });
  }

  /* The row shown in the drawer while the photo uploads. Built from what is already on the page —
     the configured mockup URL and the selected options — so opening the drawer costs no network.
     It lives inside #CartDrawer, whose innerHTML renderContents replaces wholesale on success, so
     there is no removal path to forget. */
  function pendingRow(){
    var d=document.querySelector('cart-drawer'); if(!d) return null;
    var list=d.querySelector('.ts-cd-list'); if(!list) return null;
    var li=document.createElement('li');
    li.className='cart-item ts-cd-item ts-cd-pending';
    li.id='ts-cd-pending';
    /* WHAT the customer configured is the page's vocabulary — a backdrop and a paper size on wall
       art, a fabric and a garment size on a hoodie — so the page supplies it.

       ⚠ THE CALLBACK GETS ITS OWN GUARD, AND IT WARNS. The merch page shipped a pendingRow that
       read a portrait-only variable and threw on every add; the outer bare catch swallowed it,
       taking the pending row, opts.beforeAdd AND the drawerError('') clear down with it, with no
       console trace (2026-09-01). A broken page callback must cost its own feature only — and
       audibly, so the next drift is a warning in the console instead of a customer report. */
    var row = {};
    try{ if(opts.pendingRow) row = opts.pendingRow() || {}; }
    catch(e){ try{ console.warn('[atc] pendingRow threw — optimistic row degraded:', e); }catch(_){} }
    var spec = row.spec || [];
    var thumb = row.thumb || null;
    var img = thumb ? '<div class="ts-cd-thumb"><img src="'+thumb+'" alt=""></div>' : '<div class="ts-cd-thumb"></div>';
    li.innerHTML=img+
      '<div class="ts-cd-info">'+
        '<span class="cart-item__name ts-cd-name">'+(opts.title || '')+'</span>'+
        '<p class="ts-cd-variant">'+spec.filter(Boolean).join(' &middot; ')+'</p>'+
        '<div class="wiz-ship-track"><i id="ts-cd-pendfill"></i></div>'+
        '<p class="ts-cd-pendmsg" id="ts-cd-pendmsg">'+tr('atc_uploading', 'Uploading your photo&hellip;')+'</p>'+
      '</div>';
    list.insertBefore(li, list.firstChild);
    return li;
  }
  function pendingSet(pct, msg){
    var f=document.getElementById('ts-cd-pendfill'), m=document.getElementById('ts-cd-pendmsg');
    if(f && pct!=null) f.style.width=pct+'%';
    if(m && msg) m.textContent=msg;
  }
  function pendingClear(){
    var li=document.getElementById('ts-cd-pending');
    if(li && li.parentNode) li.parentNode.removeChild(li);
  }
  function drawerError(msg){
    var e=document.getElementById('CartDrawer-CartErrors');
    if(e) e.textContent=msg||'';
  }

  function refreshBadge(count){
    var a=document.getElementById('cart-icon-bubble'); if(!a) return;
    var b=a.querySelector('.ts-cart-badge');
    if(count>0){
      if(!b){ b=document.createElement('span'); b.className='ts-cart-badge'; b.setAttribute('aria-hidden','true'); a.insertBefore(b, a.firstChild.nextSibling); }
      b.textContent=count;
    } else if(b){ b.remove(); }
  }

  /* Is the add already in the cart? The _PrintFile uuid is unique per attempt and already travels
     on the line, so it is an idempotency key we get for free. Verified: Shopify does return
     underscore-prefixed properties in /cart.js — the prefix governs DISPLAY, not the payload. */
  function reconcile(cb){
    // With no per-submit identity there is nothing to look for — see the header. "Unknown" routes
    // the caller to "check your cart", never to a retry that would raise the quantity to 2.
    if(!identity){ cb(null); return; }
    fetch(routes.cart_url+'.js',{cache:'no-store'})
      .then(function(r){ return r.json(); })
      .then(function(cart){
        cartTotal.set(cartCountingTotal(cart)); refreshBadge(cart.item_count);
        var found=(cart.items||[]).some(function(it){
          return it.properties && it.properties._PrintFile===pendingPrintFile;
        });
        cb(found, cart);
      })
      .catch(function(){ cb(null, null); });   // null = genuinely unknown
  }

  form.addEventListener('submit', function(e){
    if(submitting){ e.preventDefault(); return; }
    clearAddError();

    /* ⚠ "NO PHOTO CHOSEN" AND "THIS PRODUCT TAKES NO PHOTO" ARE DIFFERENT, AND THIS READ THEM THE
       SAME WAY. The guard was `!(file && file.files.length)`, which is true both when the customer
       has not picked a photo AND when the page has no upload field at all. Every product had one
       when it was written; the pet parent mugs do not, so `file` was null, the guard fired, and
       add-to-cart did nothing — and did it SILENTLY, because `upErr` and `upWrap` are null on those
       pages too, so there was no message either. Both products were live and unbuyable.

       So the question is asked in two parts: a product with no photo field skips this entirely, and
       one that HAS a field must still have something in it. That second half is what protects every
       other product, and it keeps exactly the teeth it had. */
    if(file && !(file.files && file.files.length)){
      e.preventDefault();
      track('add_blocked');
      if(upErr) upErr.hidden=false;
      if(upWrap) upWrap.classList.add('is-invalid');
      try{ (upWrap||file).scrollIntoView({behavior:'smooth',block:'center'}); }catch(_){}
      return;
    }
    if(file && file.files[0] && file.files[0].size>MAX_UPLOAD_BYTES){
      e.preventDefault();
      track('add_blocked');
      showAddError(tr('atc_too_big', 'That photo is {mb}MB. Please use one under 20MB.', { mb: (file.files[0].size/1048576).toFixed(1).replace('.', I18N.decimal || '.') }));
      return;
    }

    /* Decided HERE, at submit, not at load: this script is inline and parses before the deferred
       cart-drawer.js has defined renderContents. By the time anyone has clicked through three
       steps and picked a photo it is always ready. */
    var drawer=document.querySelector('cart-drawer');
    var ajaxOK = drawer && typeof drawer.renderContents==='function'
                 && window.FormData && window.XMLHttpRequest && ('upload' in new XMLHttpRequest());

    if(!ajaxOK){
      // Original behaviour, byte for byte: native multipart POST, browser navigates to /cart.
      mint(); if (opts.stampPreview) opts.stampPreview();
      track('add');
      // The label names what is actually happening: a product with no upload is not uploading.
      submitting=true; busy(true); setLabel(file ? tr('atc_uploading', 'Uploading your photo…') : tr('atc_adding', 'Adding to cart…'));
      return;
    }

    e.preventDefault();

    /* With no identity there is no per-submit key to carry, and nothing to restore on a retry —
       which is also why `reconcile` answers "unknown" and a hoodie is never offered one. */
    if(printFileEl){
      if(pendingPrintFile==null){ mint(); pendingPrintFile=printFileEl.value; }
      else { printFileEl.value=pendingPrintFile; }   // retry of a PROVEN-failed attempt
    }
    if (opts.stampPreview) opts.stampPreview();                                // always restamp: the config may have changed

    track('add');
    // Same rule as the fallback above: a product with no upload field is not uploading anything.
    var busyWord = file ? tr('atc_uploading', 'Uploading your photo…') : tr('atc_adding', 'Adding to cart…');
    submitting=true; busy(true); setFill(0); setLabel(busyWord);
    announce(file ? tr('atc_uploading_sr', 'Uploading your photo') : tr('atc_adding_sr', 'Adding to cart'));

    /* OPEN THE DRAWER NOW, not when the response lands. The upload is seconds long on mobile data
       and there is nothing useful to look at on the product page while it runs, so the click used
       to appear to do nothing but change a button.

       ⚠ is-empty has to be cleared by hand. cart-drawer.js:80 clears it from `.drawer__inner`, but
       in this drawer the class sits on the <cart-drawer> HOST — and renderContents only replaces
       #CartDrawer's innerHTML, so the host class survives every swap. Without this line a customer
       whose cart was empty when the page loaded gets the "Nothing here yet" panel over the item
       they just added, with the footer hidden. */
    try{
      drawer.classList.remove('is-empty');
      var dItems=drawer.querySelector('cart-drawer-items');
      if(dItems) dItems.classList.remove('is-empty');
      drawer.open(addBtn);
      pendingRow();
      /* Snapshot anything the caller needs to remember about THIS submit, while the form still
         holds it. The 2xx path clears the print-file input before afterAdd runs — deliberately, so
         a retry mints a fresh token — so a hook that reads the form afterwards reads an empty one.
         Own guard, same reason as pendingRow's: a page callback must fail its own feature, loudly. */
      try{ if (opts.beforeAdd) opts.beforeAdd(); }
      catch(e){ try{ console.warn('[atc] beforeAdd threw:', e); }catch(_){} }
      drawerError('');
    }catch(_){}

    /* Resize before building the payload, so FormData holds the smaller blob. Async, which is safe
       because preventDefault has already run — nothing is waiting on this handler returning.

       ⚠ AND ONLY WHERE THERE IS A PHOTO. `shrinkPhoto(file.files[0])` throws outright when the
       product has no upload field, which is the second half of the same assumption the guard above
       made: that every product takes a photo. The pet parent mugs take none, so the payload is built
       and sent directly. */
    var buildAndSend = function(photo){
      var fd=new FormData(form);
      // .set, not .append: the form already contributed the original file under this name.
      if(photo) fd.set('properties[Pet photo]', photo, photo.name||'photo.jpg');
      fd.append('sections','cart-drawer,cart-icon-bubble');
      fd.append('sections_url', window.location.pathname);
      try{ drawer.setActiveElement(addBtn); }catch(_){}   // so close() returns focus to the button
      sendAdd(fd, drawer);
    };
    if(file && file.files[0]) shrinkPhoto(file.files[0]).then(buildAndSend);
    else buildAndSend(null);
    return;
  });

  /* ── Shopify Standard Events ────────────────────────────────────────────────────────────────
     THIS PATH USED TO REPORT NOTHING. Every live portrait and hoodie adds through the XHR below,
     and only the gift card goes through Dawn's product-form.js — which is the one file that emits
     cart:lines-update. So Shopify's own funnel counted the gift card and nothing else, and
     "added to cart" read near zero on a store where it was the most common action there is.

     ⚠ THE PUB/SUB IS DELIBERATELY NOT PUBLISHED HERE, unlike product-form.js:80. It is a DOM-sync
     bus, not analytics, and both of its subscribers are already satisfied on this path: the PDP's
     CART_BASE is set through opts.cartTotal a few lines below, and cart.js's subscriber calls
     onCartUpdate(), which re-fetches ?section_id=cart-drawer and re-renders the panel that
     drawer.renderContents() has just painted from the response we already hold. Publishing would
     buy a redundant round trip and a second repaint in the middle of the slide-in, which is exactly
     the round trip the comment further down explains was removed on purpose.

     The event contract is Dawn's, copied rather than invented: dispatch before the request with a
     deferred promise, resolve it with the post-mutation cart, reject it on every failure branch. It
     is the reject that matters as much as the resolve — an unresolved promise leaves the event
     pending forever and the analytics layer holding it. */
  function standardEvents(){ return window.StandardEvents || {}; }

  function linesAddEvent(){
    var CLUE=standardEvents().CartLinesUpdateEvent;
    if(!CLUE) return null;                                   // CDN blocked or still in flight
    var idEl=form.querySelector('[name="id"]');
    if(!idEl || !idEl.value) return null;
    var deferred=CLUE.createPromise();
    /* Dispatched on the form because that is what is in the DOM here; product-form.js uses its own
       custom element for the same reason. The event bubbles either way. Quantity is literally 1: no
       live product page ships a quantity input. */
    form.dispatchEvent(new CLUE({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: idEl.value, quantity: 1 }],
      promise: deferred.promise
    }));
    return deferred;
  }

  function resolveLinesAdd(deferred){
    if(!deferred) return;
    var CLUE=standardEvents().CartLinesUpdateEvent;
    if(!CLUE) return;
    /* /cart/add.js returns the added LINE, not the cart, so the aggregates the event needs are not
       in the response we already have. CartItems.fetchCartData() de-duplicates concurrent callers,
       and this runs after the drawer has painted, so it is off the critical path rather than in
       front of it. */
    var pending = (typeof CartItems!=='undefined' && CartItems.fetchCartData)
      ? CartItems.fetchCartData()
      : fetch(routes.cart_url+'.json').then(function(r){ return r.json(); });
    pending.then(function(cart){
      if(!cart || !cart.currency){ deferred.reject(new Error('Missing currency in cart response')); return; }
      deferred.resolve({ cart: CLUE.createCartFromAjaxResponse(cart) });
    }).catch(function(e){ deferred.reject(e); });
  }

  function sendAdd(fd, drawer){
    var linesAdd=linesAddEvent();
    var xhr=new XMLHttpRequest();
    xhr.open('POST', routes.cart_add_url);
    xhr.setRequestHeader('Accept','application/javascript');
    xhr.setRequestHeader('X-Requested-With','XMLHttpRequest');
    // NEVER set Content-Type: the browser has to add the multipart boundary itself.
    // NO xhr.timeout: a 20MB photo on 3G legitimately takes minutes and a fixed timeout would kill
    // valid orders.

    var lastMilestone=0;
    xhr.upload.onprogress=function(ev){
      if(!ev.lengthComputable){ addBtn.classList.add('is-waiting'); return; }
      var pct=Math.floor(ev.loaded/ev.total*100);
      setFill(pct); setLabel(tr('atc_upload_pct', 'Uploading… {pct}%', { pct: pct }));
      pendingSet(pct, tr('atc_upload_photo_pct', 'Uploading your photo… {pct}%', { pct: pct }));
      if(pct>=lastMilestone+25){ lastMilestone=pct-(pct%25); announce(tr('atc_uploaded_pct', '{pct}% uploaded', { pct: pct })); }
    };
    /* The bytes are in the socket buffer, not necessarily at Shopify. On a slow uplink the bar can
       sit at 100% for a while, and a STATIC full bar reads as frozen — which is what makes people
       tap again. Hence its own label and a pulse. */
    xhr.upload.onload=function(){
      addBtn.classList.add('is-waiting'); setFill(100);
      setLabel(tr('atc_adding', 'Adding to cart…')); announce(tr('atc_upload_done', 'Upload complete, adding to cart'));
      pendingSet(100, tr('atc_adding_row', 'Adding to your cart&hellip;'.replace('&hellip;','…')));
    };

    function parseBody(){ try{ return JSON.parse(xhr.responseText); }catch(_){ return null; } }

    xhr.onload=function(){
      var body=parseBody();
      if(xhr.status>=200 && xhr.status<300 && body && !body.status){
        track('added');
        pendingPrintFile=null; if(printFileEl) printFileEl.value=''; if(previewEl) previewEl.value='';
        /* Every line's price, including one that ships nothing: Shopify's TOTAL_PRICE condition
           counts them, so the bar must too. This used to add 0 for a non-shippable line, which made
           adding a Digital File or a gift card move the customer no closer to a threshold it was in
           fact moving them toward. See cartCountingTotal above. */
        cartTotal.set(cartTotal.get() + (body.final_line_price || body.line_price || 0));
        setFill(100); setLabel(tr('atc_added', 'Added')); announce(tr('atc_added_sr', 'Added to cart.'));

        /* renderContents FIRST. update() is the product page's full recompute — renderPreview,
           swapPhoto, positionName, updateThumbs, plus forced layout via getComputedStyle and
           offsetWidth — and none of it is needed to paint the drawer. Running it first put all of
           that work ahead of the two chained macrotasks that actually reveal the panel. */
        try{ drawer.renderContents(body); }catch(_){ window.location=routes.cart_url; return; }
        try{ if (opts.onCartChanged) opts.onCartChanged(); }catch(_){}                      // re-runs updateFreeShip with the new base

        /* No follow-up /cart.js fetch. It was a second full round trip on every add, in flight
           during the slide, to re-derive two things we already have: the badge came back rendered
           in this same response as the cart-icon-bubble section, and the cart total is updated above
           from final_line_price. The cartUpdate subscription still corrects drift if the customer
           edits quantities in the drawer. */
        resolveLinesAdd(linesAdd);

        /* ── A SECOND LINE, IF ONE WAS ASKED FOR ─────────────────────────────────────────────────
           The $19 Digital Files add-on. It cannot ride in the call above: this payload is the form's
           own FormData, unmodified, because it carries the photo — there is no items[] seam here and
           hand-building one would start submitting fields the customer turned off (see the header).

           SEQUENCED AFTER THE 2xx, deliberately. A failed portrait must never leave a paid add-on
           stranded in the cart, so the second call only happens once the first has certainly landed.
           It owns its own errors and its own re-render; the button is already released below either
           way, because the portrait — the thing the customer actually pressed the button for — is
           in the cart whatever the add-on does. */
        try{ if (opts.afterAdd) opts.afterAdd(); }catch(_){}

        setTimeout(function(){ resetAddBtn(); submitting=false; }, 150);
        return;
      }
      // A parseable non-2xx means Shopify rejected it and the cart was NOT touched.
      if(linesAdd) linesAdd.reject(new Error((body && (body.description||body.message)) || 'Add to cart rejected'));
      pendingClear();
      var looksLikeHtml=/^\s*</.test(xhr.responseText||'');
      var msg=(body && (body.description||body.message))
              || (looksLikeHtml ? tr('atc_session', 'Your session expired. Please reload the page and try again.')
                                : ((window.cartStrings && cartStrings.error) || tr('atc_generic', 'Something went wrong. Please try again.')));
      // Both surfaces: the drawer is open and in front of the button, so an error only on the
      // button behind it would be invisible.
      track('add_failed');
      showAddError(msg); drawerError(msg);
      resetAddBtn(); setLabel(tr('atc_try_again', 'Try again')); submitting=false;
    };

    function ambiguous(){
      /* Rejected up front and not per-outcome. Even the "it landed after all" branch below cannot
         resolve honestly: the cart it would report is one we never saw a response for. */
      if(linesAdd) linesAdd.reject(new Error('Network error during add to cart'));
      setLabel(tr('atc_checking', 'Checking…'));
      reconcile(function(found){
        if(found===true){
          track('added');
          /* It landed. There is no sections payload to render, so fetch the markup separately —
             no re-upload. Verified feasible: sections/cart-icon-bubble.liquid is requested this way
             by Dawn from pages where it is not present. */
          pendingPrintFile=null; if(printFileEl) printFileEl.value=''; if(previewEl) previewEl.value='';
          fetch(window.location.pathname+'?sections=cart-drawer,cart-icon-bubble')
            .then(function(r){ return r.json(); })
            .then(function(sections){ drawer.renderContents({sections:sections}); resetAddBtn(); submitting=false; })
            .catch(function(){ window.location=routes.cart_url; });
        } else if(found===false){
          track('add_failed');
          pendingClear();
          var m1=tr('atc_upload_failed', 'Upload failed. Check your connection and try again.');
          showAddError(m1); drawerError(m1);
          resetAddBtn(); setLabel(tr('atc_try_again', 'Try again')); submitting=false;
        } else {
          /* Reconciliation itself failed, so we genuinely do not know. Offering a normal-looking
             retry here is how you get double orders. Say so instead, and leave `submitting` true. */
          pendingClear();
          var m2=tr('atc_unconfirmed', 'We could not confirm whether your portrait was added. Open your cart to check before trying again.');
          showAddError(m2); drawerError(m2);
          setLabel(tr('atc_check_cart', 'Check your cart'));
        }
      });
    }
    xhr.onerror=ambiguous;
    xhr.onabort=ambiguous;

    xhr.send(fd);
  }

  /* bfcache restore. The old reason for this (a full navigation to /cart) is gone, but it matters
     MORE now: the drawer's checkout button navigates away, and a customer who leaves mid-upload
     leaves `submitting` true and the button reading "Uploading…". iOS restores that heap intact,
     so Add to cart would be dead for the rest of the session. */
  window.addEventListener('pageshow', function(ev){
    if(!ev.persisted) return;
    resetAddBtn(); clearAddError(); submitting=false;
    if(pendingPrintFile) reconcile(function(found){ if(found===true) pendingPrintFile=null; });
  });



  track('view');
  // Handed back so the page's own cart subscriber can repaint the badge without a second copy of it.
  return { refreshBadge: refreshBadge };
};
